import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import fetchJson from '../hooks/fetchJson';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, token } = useAuth();

  // Fetch permissions for the current user
  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions({});
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: permissionsData, error } = await fetchJson(`/api/staff-permissions/my-permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (error) {
        throw new Error(error);
      }
      
      // Convert array to object for easier access
      const permissionsMap = {};
      permissionsData.forEach(permission => {
        permissionsMap[permission.page_path] = permission.is_allowed;
      });

      setPermissions(permissionsMap);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err.message);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  // Check if user has permission for a specific page
  const hasPermission = (pagePath) => {
    // Owners have all permissions
    if (user?.role === 'owner') {
      return true;
    }

    // Staff members need explicit permission
    if (user?.role === 'staff') {
      return permissions[pagePath] === true;
    }

    // Default to false for other roles
    return false;
  };

  // Check multiple permissions at once
  const hasAnyPermission = (pagePaths) => {
    return pagePaths.some(pagePath => hasPermission(pagePath));
  };

  // Check all permissions at once
  const hasAllPermissions = (pagePaths) => {
    return pagePaths.every(pagePath => hasPermission(pagePath));
  };

  // Refresh permissions
  const refreshPermissions = () => {
    fetchPermissions();
  };

  // Effect to fetch permissions when user changes
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionContext.Provider value={{
      permissions,
      loading,
      error,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      refreshPermissions
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
